/**
 * The organization this builder tab is currently acting in.
 *
 * Held in memory only — never localStorage — so two builder tabs open on two different
 * organizations do not clobber each other. Seeded from the loaded document's org and sent
 * as X-Organization-Id on every API call so the backend scopes each request to the org the
 * document actually belongs to, regardless of the session's last-active org.
 *
 * The builder learns its org from the first document fetch, so there is a window before the
 * org is known. When a document-scoped route arms the gate (`armOrgGate`), org-scoped calls
 * block on `whenOrgReady()` until the document fetch seeds the org — instead of silently
 * falling back to the session's default org. Pre-document flows (e.g. the from-scratch agent
 * route) never arm it, so they proceed immediately on the session default, which is correct
 * there because no document org exists yet.
 */
let activeOrgId: number | null = null;

// Ready by default: only document-scoped routes that will establish an org arm the gate.
let isReady = true;
let resolveReady: () => void = () => {};
let readyPromise = Promise.resolve();

export function setActiveOrgId(orgId: number | null): void {
  activeOrgId = orgId != null ? Number(orgId) : null;
  if (!isReady) {
    isReady = true;
    resolveReady();
  }
}

export function getActiveOrgId(): number | null {
  return activeOrgId;
}

// Arm the gate before loading a document whose org must scope subsequent calls. Clears any
// previously seeded org so a client-side navigation to another document can't reuse the old
// org, and makes org-scoped calls wait until the new document's fetch seeds it.
export function armOrgGate(): void {
  activeOrgId = null;
  isReady = false;
  readyPromise = new Promise<void>((resolve) => {
    resolveReady = resolve;
  });
}

export function whenOrgReady(): Promise<void> {
  return isReady ? Promise.resolve() : readyPromise;
}
